import { useState } from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { Modal } from '~/components/overlays';
import { useDeleteLocation } from '~/api/location';
import { extractGraphQLErrorMessages } from '~/utils';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  location: { id: string; name: string };
  onDeleted: (id: string) => void;
};

export function DeleteLocationDialog({
  isOpen,
  onClose,
  location,
  onDeleted,
}: Props) {
  const { t } = useTranslation('settings');
  const [isDeleting, setIsDeleting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const deleteLocation = useDeleteLocation();

  function handleClose() {
    setErrorMessage(null);
    setIsDeleting(false);
    onClose();
  }

  async function handleConfirm() {
    setIsDeleting(true);
    setErrorMessage(null);
    try {
      await deleteLocation.mutateAsync({ id: location.id });
      toast.success(t('settings.facilities.locations.delete_success'));
      onDeleted(location.id);
      handleClose();
    } catch (err: unknown) {
      const message =
        err instanceof Error
          ? extractGraphQLErrorMessages(err).join(', ')
          : t('settings.facilities.locations.delete_error');
      setErrorMessage(message);
      setIsDeleting(false);
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={handleClose}>
      <div className="bg-neut-bg-alt rounded-lg p-6 w-full max-w-sm">
        <h2
          id="Modal-title"
          className="text-base font-semibold text-neut-prim mb-2"
        >
          {t('settings.facilities.locations.delete_dialog.title', {
            name: location.name,
          })}
        </h2>
        <p className="text-sm text-neut-sec mb-4">
          {t('settings.facilities.locations.delete_dialog.message')}
        </p>

        {errorMessage && (
          <p className="text-sm text-error-500 mb-4">{errorMessage}</p>
        )}

        <div className="flex gap-3">
          <button
            type="button"
            onClick={handleClose}
            disabled={isDeleting}
            className="flex-1 px-4 py-2 text-sm font-medium text-neut-prim border border-neut-200 rounded-lg hover:bg-neut-50 transition-colors disabled:opacity-50"
          >
            {t('settings.facilities.locations.delete_dialog.cancel')}
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={isDeleting}
            className="flex-1 px-4 py-2 text-sm font-medium text-white bg-error-500 rounded-lg hover:bg-error-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {t('settings.facilities.locations.delete_dialog.confirm')}
          </button>
        </div>
      </div>
    </Modal>
  );
}
